import { useEffect, useState } from "react"
import { SubmittalProps } from "../../../interfaces/submittal_types"
import { Error_Component } from "../../misc"
import { SubmittalFormBase } from "./SubmittalFormBase"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { ProjectSchema } from "../../_Shared/Form/Schemas/ProjectSchema"
import { AssignedToSchema } from "../../_Shared/Form/Schemas/ManagerSchema"
import { AssignedBySchema } from "../../_Shared/Form/Schemas/ManagerSchema"
import { CategorySchema } from "../../_Shared/Form/Schemas/Category"
import { StatusSchema } from "../../_Shared/Form/Schemas/StatusSchema"
import { useFormData } from "@/components/_Shared/Form/Hooks/useFormDataFetch"
import { Alert } from "@/components/_Shared/Form/Buttons/Alert"
import { useAuth } from "@/App"
import { CreateOrUpdate } from "@/components/_Shared/Form/Utils/MethodHandler"
import { createSubmittal, updateSubmittal } from "@/api/submittal"
import { EmployeeProps } from "@/interfaces/employee_type"
import { ImportSubmittalProps } from "./Utils/ImportSubmittalProps"
import { DefaultSubmittalProps } from "./Utils/DefaultSubmittalProps"

const submittalFormSchema = z.object({
    submittal_id: z.string().optional(), // Used for PUT requests
    received_date: z.string().min(1, "Received date is required"),
    sub_description: z.string().min(1, "Description is required"),
    alert_status: z.boolean().optional(),
    folder_location: z.string().optional(),
    completed_at: z.string().optional(),
})
.merge(ProjectSchema)
.merge(AssignedToSchema)
.merge(AssignedBySchema)
.merge(CategorySchema)
.merge(StatusSchema)

export type SubmittalFormData = z.infer<typeof submittalFormSchema>

export function SubmittalForm({submittal, method}: {submittal?: SubmittalProps, method: "POST" | "PUT"}) {
    const [FolderCreated, setFolderCreated] = useState(false)
    const { user } = useAuth()
    const { fetchAllData, projectManagers, categories, projects } = useFormData()

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isSubmitting, isSubmitSuccessful },
        setError
    } = useForm<SubmittalFormData>({
        resolver: zodResolver(submittalFormSchema),
        defaultValues: submittal ? ImportSubmittalProps({submittal}) : DefaultSubmittalProps(user as EmployeeProps)
    })
    
    const onSubmit = async (data: SubmittalFormData) => {
        const submittalData: SubmittalProps = {
            ...data,
            assigned_to: data.assigned_to?.value ?? "",
            assigned_by: data.assigned_by?.value ?? "",
            project: data.project?.value ?? "",
            category: data.category?.value as "Other" | "Electrical" | "Mechanical" | "Plumbing" | "Fire Protection",
            status: data.status?.value as "Active" | "Closed",
            received_date: data.received_date ?? "",
            sub_description: data.sub_description ?? "",
            submittal_id: data.submittal_id ?? "",
            folder_location: data.folder_location ?? "",
            completed_at: data.completed_at ?? "",
        }

        const method_handler = CreateOrUpdate(method, createSubmittal, updateSubmittal)

        if (method_handler) {
            const response = await method_handler(submittalData)

            if (response.status === 400) {
                setError("root", {message: "An error occurred while submitting the submittal form."})
            } else if (response.status === 403) {
                setError("root", {message: "You are not authorized to submit this submittal."})
            } else if (response.status === 500) {
                setError("root", {message: "An error occurred while submitting the submittal form."})
            } 

            if (response.data && 'folder_created' in response.data) {
                setFolderCreated(response.data.folder_created)
            }
        } else {
            setError("root", {message: "An error occurred"})
        }
    }

    useEffect(() => {
        fetchAllData()
    }, [fetchAllData])

    return (
        <>
            {errors.root && <Error_Component errorString={errors.root?.message ?? "An error occurred"}/>}

            <SubmittalFormBase 
                submittal={submittal ?? null}
                project_list={projects}
                employee_list={projectManagers}
                category_list={categories}
                onSubmit={handleSubmit(onSubmit)}
                method={method}
                isProcessing={isSubmitting}
                register={register}
                errors={errors}
                control={control}
            />

            {isSubmitSuccessful && <Alert 
                title={`Submittal ${method == "POST" ? "created" : "updated"} successfully`}
                description={setDescription(method, FolderCreated)}
                redirects={[
                    {label: "To Submittals", value: "/submittals"},
                    {label: "To Dashboard", value: "/main_menu"},
                    {label: "Create a new submittal", value: "/submittals/create_submittal"}
                ]}
            />}
        </>
    )
}

// Just an easy way to set the description of the alert based on the method and if the folder was created successfully
function setDescription(method: "POST" | "PUT", folder_created: boolean) {
    if (method === "POST" && folder_created) {
        return "The submittal has been created successfully."
    } else if (method === "POST" && !folder_created) {
        return "The submittal has been created successfully, however, there was an error creating the folder. Please manually create the folder."
    } else if (method === "PUT") {
        return "The submittal has been updated successfully."
    } else {
        return "An error occurred while submitting the submittal form."
    }
}